import React from "react";
import { useBrand } from "@/lib/brand";

function LegalShell({ title, eyebrow, children }) {
  return (
    <div className="bg-[#05070C] text-[#E8ECF3] min-h-[70vh]">
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-24">
        <div className="label-eyebrow">{eyebrow}</div>
        <h1 className="font-display text-4xl md:text-6xl mt-4">{title}</h1>
        <div className="w-16 h-[2px] bg-[#2DD4BF] mt-6" />
        <div className="mt-12 space-y-6 text-[#CBD5E1] leading-relaxed">
          {children}
        </div>
      </div>
    </div>
  );
}

export function TermsPage() {
  const brand = useBrand();
  return (
    <LegalShell eyebrow="Legal" title="Terms & Conditions">
      <p className="text-sm text-[#64748B]">Last updated: 15 February 2026</p>
      <h3 className="font-display text-xl text-[#E8ECF3] mt-8">1. Acceptance</h3>
      <p>By using {brand.name}, you agree to these Terms. If you don't agree, please don't use the platform.</p>

      <h3 className="font-display text-xl text-[#E8ECF3] mt-4">2. Who can use the platform</h3>
      <p>You must be 18+ to register. Retired professionals joining as experts must have verifiable professional history.</p>

      <h3 className="font-display text-xl text-[#E8ECF3] mt-4">3. Bookings & payments</h3>
      <p>Clients pay via Razorpay at the time of booking. {brand.name} charges a 12% platform fee. The remaining amount is paid to the expert after session completion.</p>

      <h3 className="font-display text-xl text-[#E8ECF3] mt-4">4. Refunds & cancellations</h3>
      <p>Cancellations more than 24 hours before the session are fully refundable. Within 24 hours, refunds are at the expert's discretion. Contact {brand.email} for any dispute.</p>

      <h3 className="font-display text-xl text-[#E8ECF3] mt-4">5. Conduct</h3>
      <p>Users agree to be respectful, honest and lawful in all sessions and communications. Harassment, fraud or misrepresentation will lead to permanent removal.</p>

      <h3 className="font-display text-xl text-[#E8ECF3] mt-4">6. Intellectual property</h3>
      <p>Content created by experts during sessions belongs to the expert unless otherwise agreed. Platform content and branding is owned by {brand.name}.</p>

      <h3 className="font-display text-xl text-[#E8ECF3] mt-4">7. Liability</h3>
      <p>{brand.name} is a marketplace and does not guarantee outcomes of any consultation. Advice given by experts is their own professional opinion.</p>

      <h3 className="font-display text-xl text-[#E8ECF3] mt-4">8. Governing law</h3>
      <p>These terms are governed by the laws of India. Any disputes fall under the jurisdiction of courts in Indore, Madhya Pradesh.</p>

      <h3 className="font-display text-xl text-[#E8ECF3] mt-4">9. Contact</h3>
      <p>For any legal queries: <a className="text-[#2DD4BF]" href={`mailto:${brand.email}`}>{brand.email}</a></p>
    </LegalShell>
  );
}

export function PrivacyPage() {
  const brand = useBrand();
  return (
    <LegalShell eyebrow="Legal" title="Privacy Policy">
      <p className="text-sm text-[#64748B]">Last updated: 15 February 2026</p>
      <h3 className="font-display text-xl text-[#E8ECF3] mt-8">Data we collect</h3>
      <p>Name, email, phone, professional history (for experts), booking history and payment metadata (via Razorpay).</p>

      <h3 className="font-display text-xl text-[#E8ECF3] mt-4">How we use it</h3>
      <p>To run the marketplace: authenticate you, match you with the right expert/client, process payments, send booking notifications, and respond to your queries.</p>

      <h3 className="font-display text-xl text-[#E8ECF3] mt-4">Data we DON'T sell</h3>
      <p>We do not sell your personal data to any third party. Ever.</p>

      <h3 className="font-display text-xl text-[#E8ECF3] mt-4">Payment security</h3>
      <p>All payments are processed by Razorpay (PCI-DSS Level 1). We do not store card numbers, UPI PINs or bank credentials on our servers.</p>

      <h3 className="font-display text-xl text-[#E8ECF3] mt-4">Your rights</h3>
      <p>You can request deletion of your account and personal data anytime by emailing <a className="text-[#2DD4BF]" href={`mailto:${brand.email}`}>{brand.email}</a>. We will comply within 30 days.</p>

      <h3 className="font-display text-xl text-[#E8ECF3] mt-4">Cookies</h3>
      <p>We use a session cookie to keep you logged in. No third-party trackers or advertising cookies.</p>
    </LegalShell>
  );
}

export function FAQPage() {
  const brand = useBrand();
  const faqs = [
    { q: "Who can join as an expert?", a: "Any retired professional with 15+ years of verifiable experience in their field. School teachers, retired executives, doctors, lawyers, engineers — all welcome." },
    { q: "How is verification done?", a: "Our admin team manually reviews every expert profile within 24 hours. We check credentials, references and career history before granting the green Verified badge." },
    { q: "How does the AI Match work?", a: "You describe your need in plain English. Our AI (Claude Sonnet 4.5) reads it and shortlists 3 verified experts most likely to help — based on their expertise, experience and past reviews." },
    { q: "How much does it cost?", a: "Free to browse and free to create profiles. Clients pay only when booking a session — rates range from ₹1,200/hr to ₹3,500/hr depending on the expert. 2nd Innings takes a 12% platform fee." },
    { q: "How do experts get paid?", a: "88% of every booking is transferred to the expert's bank account after session completion. Payouts happen via Razorpay within 3-5 business days." },
    { q: "Are sessions online or in-person?", a: "Currently all sessions are conducted online via Zoom/Google Meet. In-person sessions may be arranged directly between expert and client." },
    { q: "What if I'm not satisfied?", a: "Contact us within 48 hours of the session. If the complaint is valid, we will issue a full refund from our platform fee and pause the expert's profile pending investigation." },
    { q: "Is there an app?", a: "The website is fully mobile-responsive. A native app is on our roadmap for Q3 2026." },
  ];
  return (
    <LegalShell eyebrow="Help" title="Frequently Asked Questions">
      <div className="space-y-6">
        {faqs.map((f, i) => (
          <div key={i} className="border-b border-[#142542] pb-6">
            <div className="font-display text-xl text-[#E8ECF3]">{f.q}</div>
            <p className="text-[#94A3B8] mt-2">{f.a}</p>
          </div>
        ))}
      </div>
      <p className="mt-8">Still have questions? Write to us at <a className="text-[#2DD4BF]" href={`mailto:${brand.email}`}>{brand.email}</a></p>
    </LegalShell>
  );
}

export function ContactPage() {
  const brand = useBrand();
  return (
    <LegalShell eyebrow="Get in Touch" title="Contact 2nd Innings">
      <div className="grid md:grid-cols-2 gap-8 not-prose">
        <div className="section-card">
          <div className="label-eyebrow mb-4">Email</div>
          <a href={`mailto:${brand.email}`} className="font-display text-xl text-[#2DD4BF] break-all">{brand.email}</a>
          <p className="text-[#94A3B8] mt-3 text-sm">General queries, expert verification, refunds & press.</p>
        </div>
        <div className="section-card">
          <div className="label-eyebrow mb-4">Phone</div>
          <a href={`tel:${brand.phone}`} className="font-display text-xl text-[#2DD4BF]">{brand.phone}</a>
          <p className="text-[#94A3B8] mt-3 text-sm">Mon–Sat, 10am–7pm IST.</p>
        </div>
        <div className="section-card">
          <div className="label-eyebrow mb-4">WhatsApp</div>
          <a href={`https://wa.me/${brand.whatsapp?.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="font-display text-xl text-[#2DD4BF]">{brand.whatsapp}</a>
          <p className="text-[#94A3B8] mt-3 text-sm">Direct messages, quick queries, expert onboarding.</p>
        </div>
        <div className="section-card">
          <div className="label-eyebrow mb-4">Registered Office</div>
          <div className="text-[#E8ECF3]">{brand.address}</div>
          <p className="text-[#94A3B8] mt-3 text-sm">Visits by appointment only.</p>
        </div>
      </div>
    </LegalShell>
  );
}
